// Find-in-page for the memo view. We paint our OWN translucent highlight rectangles
// in an overlay layer (positioned from each match's geometry) rather than the CSS
// Custom Highlight API — that API registers ranges but does not reliably PAINT
// ::highlight() in some deployed browser configs (confirmed live: 15 ranges
// registered, style present, still no paint). Overlay rects work everywhere and
// never touch React-owned DOM.

/** Case-insensitive, non-overlapping [start, end) match offsets of `needle` in
 * `haystack`. Pure — the unit-tested core. */
export function findMatchIndices(haystack: string, needle: string): [number, number][] {
  const out: [number, number][] = [];
  if (!needle) return out;
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  let from = 0;
  for (;;) {
    const i = h.indexOf(n, from);
    if (i < 0) break;
    out.push([i, i + n.length]);
    from = i + n.length; // non-overlapping
  }
  return out;
}

/** Wrap a match index by `dir` (+1 next / -1 prev) over `n` matches. Pure. */
export function wrapIndex(i: number, n: number, dir: 1 | -1): number {
  if (n <= 0) return 0;
  return (i + dir + n) % n;
}

// Never match inside an open editor / form control (keeps click-to-edit intact),
// non-text nodes, or the find bar itself.
const SKIP_SELECTOR = "[contenteditable], input, textarea, script, style, .flow-memo-find";

/** Walk visible text nodes under `root` and build one Range per match of `term`. */
export function computeRanges(root: HTMLElement, term: string): Range[] {
  const ranges: Range[] = [];
  if (!term) return ranges;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Node) {
      const parent = (node as Text).parentElement;
      if (!parent || parent.closest(SKIP_SELECTOR)) return NodeFilter.FILTER_REJECT;
      if (!(node.nodeValue ?? "").trim()) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node = walker.nextNode();
  while (node) {
    const text = node.nodeValue ?? "";
    for (const [start, end] of findMatchIndices(text, term)) {
      const range = document.createRange();
      range.setStart(node, start);
      range.setEnd(node, end);
      ranges.push(range);
    }
    node = walker.nextNode();
  }
  return ranges;
}

export interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
  matchIndex: number; // which match (0-based) this rect belongs to → colors the current one
}

/** Convert match ranges into rectangles positioned relative to `container`'s box
 * (which is the overlay's positioned ancestor). A single match can span multiple
 * client rects when it wraps across lines. */
export function rectsForRanges(ranges: Range[], container: HTMLElement): HighlightRect[] {
  const base = container.getBoundingClientRect();
  const out: HighlightRect[] = [];
  ranges.forEach((range, matchIndex) => {
    const clientRects = range.getClientRects();
    for (let i = 0; i < clientRects.length; i++) {
      const r = clientRects[i];
      if (!r || r.width === 0 || r.height === 0) continue;
      out.push({
        top: r.top - base.top,
        left: r.left - base.left,
        width: r.width,
        height: r.height,
        matchIndex,
      });
    }
  });
  return out;
}
